import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import {
  ArrowLeft, Search, Download, Mail, Building2, BookOpen, Phone,
  ChevronDown, ChevronUp, Inbox, ExternalLink, Send, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const TAB_FREE_GUIDE = "free_guide";
const TAB_CORPORATE = "corporate";

export default function InstructorLeads() {
  const [data, setData] = useState({ leads: [], corporate_inquiries: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(TAB_FREE_GUIDE);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [expandedRow, setExpandedRow] = useState(null);
  const [dripStats, setDripStats] = useState(null);
  const [dripRunning, setDripRunning] = useState(false);

  const refreshDripStats = async () => {
    try {
      const { data: s } = await api.get("/admin/drip/stats");
      setDripStats(s);
    } catch {
      // Stats failed; we'll just hide the section
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/admin/leads?limit=500");
        setData(d);
      } catch {
        toast.error("Could not load lead pipeline");
      } finally {
        setLoading(false);
      }
    })();
    refreshDripStats();
  }, []);

  const runDripNow = async () => {
    setDripRunning(true);
    try {
      const { data: r } = await api.post("/admin/drip/run-due");
      toast.success(`Drip run: ${r.sent} sent · ${r.skipped} already done · ${r.checked} checked`);
      await refreshDripStats();
    } catch {
      toast.error("Drip run failed");
    } finally {
      setDripRunning(false);
    }
  };

  // Filter + sort the active list based on the current tab/query/sort state
  const visibleRows = useMemo(() => {
    const source = tab === TAB_FREE_GUIDE
      ? (data.leads || []).filter((l) => l.source === "free_guide")
      : data.corporate_inquiries || [];
    const q = query.trim().toLowerCase();
    const filtered = q
      ? source.filter((r) =>
          (r.email || "").toLowerCase().includes(q) ||
          (r.full_name || r.contact_name || "").toLowerCase().includes(q) ||
          (r.company_name || "").toLowerCase().includes(q),
        )
      : source;
    const sorted = [...filtered].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      if (av === bv) return 0;
      const cmp = av > bv ? 1 : -1;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [tab, query, sortKey, sortDir, data]);

  const setSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const downloadCsv = () => {
    if (visibleRows.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    let headers, rows;
    if (tab === TAB_FREE_GUIDE) {
      headers = ["created_at", "email", "full_name", "submit_count", "last_seen"];
      rows = visibleRows.map((l) => [
        l.created_at || "", l.email || "", l.full_name || "", l.submit_count || 1, l.last_seen || "",
      ]);
    } else {
      headers = ["created_at", "company_name", "contact_name", "email", "phone", "team_size", "interests", "message", "status"];
      rows = visibleRows.map((i) => [
        i.created_at || "", i.company_name || "", i.contact_name || "", i.email || "",
        i.phone || "", i.team_size || "", (i.interests || []).join("|"),
        (i.message || "").replace(/\n/g, " ").replace(/"/g, '""'), i.status || "new",
      ]);
    }
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => {
        const s = String(cell);
        return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const ts = new Date().toISOString().slice(0, 10);
    link.download = `apm-leads-${tab}-${ts}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${visibleRows.length} rows`);
  };

  const fmt = (iso) => iso
    ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="instructor-leads-page">
        <Link to="/instructor" className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 mb-6" data-testid="leads-back-link">
          <ArrowLeft className="w-4 h-4" /> Cohort dashboard
        </Link>

        <div className="overline mb-2"><span className="red-bar" />Instructor tools</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mb-2" data-testid="leads-title">
          Lead pipeline
        </h1>
        <p className="text-zinc-600 mb-8 max-w-2xl text-sm leading-relaxed">
          All free-guide signups and corporate-training inquiries in one place. Sortable, searchable, exportable.
        </p>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-3 mb-8">
          <div className="card-tech p-4">
            <BookOpen className="w-5 h-5 text-red-600 mb-2" />
            <div className="font-heading text-3xl font-bold tracking-tight" data-testid="leads-stat-free">
              {(data.leads || []).filter((l) => l.source === "free_guide").length}
            </div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Free-guide signups</div>
          </div>
          <div className="card-tech p-4">
            <Building2 className="w-5 h-5 text-red-600 mb-2" />
            <div className="font-heading text-3xl font-bold tracking-tight" data-testid="leads-stat-corp">
              {(data.corporate_inquiries || []).length}
            </div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Corporate inquiries</div>
          </div>
          <div className="card-tech p-4">
            <Inbox className="w-5 h-5 text-red-600 mb-2" />
            <div className="font-heading text-3xl font-bold tracking-tight" data-testid="leads-stat-total">
              {((data.leads || []).length) + ((data.corporate_inquiries || []).length)}
            </div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Total contacts</div>
          </div>
        </div>

        {/* Drip campaign panel */}
        {dripStats && (
          <div className="card-tech p-5 mb-8" data-testid="drip-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Send className="w-4 h-4 text-red-600" />
                  <div className="overline">Free-guide drip campaign</div>
                </div>
                <h2 className="font-heading text-xl font-bold tracking-tight">Lead nurture sequence</h2>
                <p className="text-xs text-zinc-600 mt-0.5">
                  4 educational emails sent at Day 3, 7, 14, 30 to every free-guide signup. Idempotent — re-running never double-sends. Runs automatically every 10 min in the background.
                </p>
              </div>
              <button
                onClick={runDripNow}
                disabled={dripRunning}
                className="btn-outline text-xs"
                data-testid="drip-run-btn"
              >
                {dripRunning ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Running…</> : <><Send className="w-3.5 h-3.5" /> Run drip now</>}
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
              <div className="border-l-2 border-red-600 pl-3 py-1" data-testid="drip-stat-enrolled">
                <div className="font-heading text-2xl font-bold tracking-tight">{dripStats.total_enrolled}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">Enrolled in drip</div>
              </div>
              <div className="border-l-2 border-zinc-300 pl-3 py-1" data-testid="drip-stat-active">
                <div className="font-heading text-2xl font-bold tracking-tight">{dripStats.active}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">Active</div>
              </div>
              <div className="border-l-2 border-zinc-300 pl-3 py-1" data-testid="drip-stat-unsubscribed">
                <div className="font-heading text-2xl font-bold tracking-tight">{dripStats.unsubscribed}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">Unsubscribed</div>
              </div>
              <div className="border-l-2 border-green-600 pl-3 py-1" data-testid="drip-stat-attributed-revenue">
                <div className="font-heading text-2xl font-bold tracking-tight">
                  ${Math.round(dripStats.total_attributed_revenue || 0).toLocaleString()}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">Attributed revenue</div>
              </div>
              <div className="border-l-2 border-green-600 pl-3 py-1" data-testid="drip-stat-attributed-enrollments">
                <div className="font-heading text-2xl font-bold tracking-tight">
                  {dripStats.total_attributed_enrollments || 0}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">Enrollments</div>
              </div>
            </div>

            <div className="border-t border-zinc-100 pt-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Performance by step</div>
              <div className="grid sm:grid-cols-4 gap-2" data-testid="drip-step-grid">
                {dripStats.steps.map((s) => {
                  const conv = s.sent_count > 0 ? ((s.enrollments || 0) / s.sent_count * 100) : 0;
                  return (
                    <div key={s.step} className="bg-zinc-50/60 px-3 py-2 border border-zinc-100" data-testid={`drip-step-${s.step}`}>
                      <div className="flex items-baseline justify-between">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-red-600 font-bold">Day {s.offset_days}</div>
                        <div className="font-heading text-lg font-bold tracking-tight">{s.sent_count}</div>
                      </div>
                      <div className="text-[11px] text-zinc-700 leading-snug mt-0.5 line-clamp-2">{s.subject}</div>
                      <div className="mt-2 pt-2 border-t border-zinc-200/70 flex items-baseline justify-between gap-2">
                        <div>
                          <div className="font-heading text-base font-bold text-green-700 tracking-tight" data-testid={`drip-step-${s.step}-revenue`}>
                            ${Math.round(s.revenue || 0).toLocaleString()}
                          </div>
                          <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">
                            {s.enrollments || 0} enroll · {conv.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Tabs + controls */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex" data-testid="leads-tabs">
            <button
              onClick={() => setTab(TAB_FREE_GUIDE)}
              className={`px-4 py-2 text-sm font-mono uppercase tracking-widest border-2 transition-colors ${tab === TAB_FREE_GUIDE ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200 hover:border-red-300"}`}
              data-testid="leads-tab-free"
            >
              <BookOpen className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Free Guide
            </button>
            <button
              onClick={() => setTab(TAB_CORPORATE)}
              className={`px-4 py-2 text-sm font-mono uppercase tracking-widest border-2 border-l-0 transition-colors ${tab === TAB_CORPORATE ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200 hover:border-red-300"}`}
              data-testid="leads-tab-corp"
            >
              <Building2 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Corporate
            </button>
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute top-3 left-3 text-zinc-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by email, name, or company…"
              className="w-full border-2 border-zinc-200 focus:border-red-600 outline-none pl-9 pr-3 py-2 text-sm bg-white"
              data-testid="leads-search-input"
            />
          </div>
          <button onClick={downloadCsv} className="btn-outline" data-testid="leads-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-sm text-zinc-500 italic py-12 text-center" data-testid="leads-loading">Loading…</div>
        ) : visibleRows.length === 0 ? (
          <div className="text-sm text-zinc-500 italic py-12 text-center border-2 border-dashed border-zinc-200" data-testid="leads-empty">
            {query ? "No matches. Clear the search to see everything." :
              tab === TAB_FREE_GUIDE
                ? "No free-guide signups yet. Share the link to /free-guide on social to start filling this pipeline."
                : "No corporate inquiries yet. Share /corporate with hiring managers."}
          </div>
        ) : (
          <div className="border border-zinc-200 overflow-x-auto" data-testid="leads-table">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <SortHeader k="created_at" sortKey={sortKey} sortDir={sortDir} onClick={setSort}>When</SortHeader>
                  <SortHeader k="email" sortKey={sortKey} sortDir={sortDir} onClick={setSort}>Email</SortHeader>
                  {tab === TAB_FREE_GUIDE ? (
                    <>
                      <SortHeader k="full_name" sortKey={sortKey} sortDir={sortDir} onClick={setSort}>Name</SortHeader>
                      <SortHeader k="submit_count" sortKey={sortKey} sortDir={sortDir} onClick={setSort}>Submits</SortHeader>
                    </>
                  ) : (
                    <>
                      <SortHeader k="company_name" sortKey={sortKey} sortDir={sortDir} onClick={setSort}>Company</SortHeader>
                      <SortHeader k="team_size" sortKey={sortKey} sortDir={sortDir} onClick={setSort}>Size</SortHeader>
                      <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Interests</th>
                    </>
                  )}
                  <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row, idx) => {
                  const isExpanded = expandedRow === (row.id || `${tab}-${idx}`);
                  return (
                    <FragmentRow
                      key={row.id || `${tab}-${idx}`}
                      row={row}
                      tab={tab}
                      idx={idx}
                      isExpanded={isExpanded}
                      onToggle={() => setExpandedRow(isExpanded ? null : (row.id || `${tab}-${idx}`))}
                      fmt={fmt}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function SortHeader({ children, k, sortKey, sortDir, onClick }) {
  const active = sortKey === k;
  return (
    <th
      onClick={() => onClick(k)}
      className={`text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest cursor-pointer select-none transition-colors ${active ? "text-red-600" : "text-zinc-600 hover:text-zinc-900"}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {active && (sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </span>
    </th>
  );
}

function FragmentRow({ row, tab, idx, isExpanded, onToggle, fmt }) {
  return (
    <>
      <tr className={`border-b border-zinc-100 last:border-b-0 ${idx % 2 ? "bg-zinc-50/30" : ""} hover:bg-red-50/30 transition-colors`} data-testid={`leads-row-${row.id || idx}`}>
        <td className="px-3 py-3 font-mono text-[11px] text-zinc-700 whitespace-nowrap">{fmt(row.created_at)}</td>
        <td className="px-3 py-3 font-mono text-xs">
          <a href={`mailto:${row.email}`} className="text-zinc-900 hover:text-red-600 inline-flex items-center gap-1">
            <Mail className="w-3 h-3" /> {row.email}
          </a>
        </td>
        {tab === TAB_FREE_GUIDE ? (
          <>
            <td className="px-3 py-3 text-sm">{row.full_name || <span className="text-zinc-400 italic">—</span>}</td>
            <td className="px-3 py-3 text-sm font-mono">{row.submit_count || 1}</td>
          </>
        ) : (
          <>
            <td className="px-3 py-3 text-sm font-semibold">{row.company_name}</td>
            <td className="px-3 py-3 text-sm font-mono">{row.team_size}</td>
            <td className="px-3 py-3 text-xs">
              <div className="flex flex-wrap gap-1">
                {(row.interests || []).slice(0, 3).map((i) => (
                  <span key={i} className="px-1.5 py-0.5 bg-red-100 text-red-700 font-mono text-[10px]">{i}</span>
                ))}
                {(row.interests || []).length > 3 && (
                  <span className="text-zinc-500">+{(row.interests || []).length - 3}</span>
                )}
              </div>
            </td>
          </>
        )}
        <td className="px-3 py-3 text-right">
          <button onClick={onToggle} className="text-xs font-mono text-zinc-500 hover:text-red-600 transition-colors">
            {isExpanded ? "Hide" : "Detail"}
          </button>
        </td>
      </tr>
      {isExpanded && (
        <tr className="bg-zinc-50 border-b border-zinc-100">
          <td colSpan={6} className="px-6 py-4">
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              {tab === TAB_FREE_GUIDE ? (
                <>
                  <DetailField label="Lead ID">{row.id}</DetailField>
                  <DetailField label="Last seen">{fmt(row.last_seen)}</DetailField>
                  <DetailField label="Source">{row.source}</DetailField>
                  <DetailField label="Submit count">{row.submit_count || 1}</DetailField>
                </>
              ) : (
                <>
                  <DetailField label="Inquiry ID">{row.id}</DetailField>
                  <DetailField label="Phone">
                    {row.phone ? <a href={`tel:${row.phone}`} className="text-red-600 hover:underline inline-flex items-center gap-1"><Phone className="w-3 h-3" />{row.phone}</a> : <span className="text-zinc-400 italic">none provided</span>}
                  </DetailField>
                  <DetailField label="All interests">
                    <div className="flex flex-wrap gap-1">
                      {(row.interests || []).map((i) => (
                        <span key={i} className="px-1.5 py-0.5 bg-red-100 text-red-700 font-mono text-[10px]">{i}</span>
                      ))}
                    </div>
                  </DetailField>
                  <DetailField label="Status">
                    <span className="px-2 py-0.5 bg-zinc-200 text-zinc-700 font-mono text-[10px] uppercase tracking-wider">{row.status || "new"}</span>
                  </DetailField>
                  {row.message && (
                    <div className="sm:col-span-2">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Message</div>
                      <p className="text-sm bg-white p-3 border-l-2 border-red-600 leading-relaxed">{row.message}</p>
                    </div>
                  )}
                </>
              )}
              <div className="sm:col-span-2 pt-2 border-t border-zinc-200 flex gap-2 flex-wrap">
                <a
                  href={`mailto:${row.email}?subject=${encodeURIComponent("Following up — APM Fire Alarm Exam Prep")}`}
                  className="btn-outline text-xs"
                  data-testid={`leads-reply-${row.id}`}
                >
                  <Mail className="w-3.5 h-3.5" /> Reply via email
                </a>
                {tab === TAB_CORPORATE && (
                  <a
                    href={`mailto:${row.email}?subject=${encodeURIComponent(`Proposal for ${row.company_name}`)}&body=${encodeURIComponent(`Hi ${(row.contact_name || "").split(" ")[0]},\n\nThanks for reaching out about training for ${row.company_name}. I'd love to put together a custom proposal — here's what I'm thinking:\n\n[...]\n\nLooking forward to chatting,\nTrevor`)}`}
                    className="btn-outline text-xs"
                    data-testid={`leads-proposal-${row.id}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Draft proposal email
                  </a>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DetailField({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <div className="text-sm text-zinc-800 break-all">{children}</div>
    </div>
  );
}
